/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    code: "code",
    em: "em",
    strong: "strong",
    a: "a",
    ul: "ul",
    li: "li"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Coming from CSS"
    }), "\n", _jsx(_components.h3, {
      children: "How do Styled Components work within a component?"
    }), "\n", _jsx(_components.p, {
      children: "If you're familiar with importing CSS into your components (e.g. like CSSModules) you'll be used to doing something like this:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import React from 'react';\nimport styles from './styles.css';\n\nexport default class Counter extends React.Component {\n  state = { count: 0 };\n\n  increment = () => this.setState({ count: this.state.count + 1 });\n  decrement = () => this.setState({ count: this.state.count - 1 });\n\n  render() {\n    return (\n      <div className={styles.counter}>\n        <p className={styles.paragraph}>{this.state.count}</p>\n        <button className={styles.button} onClick={this.increment}>\n          +\n        </button>\n        <button className={styles.button} onClick={this.decrement}>\n          -\n        </button>\n      </div>\n    );\n  }\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Because a Styled Component is the ", _jsx(_components.em, {
        children: "combination"
      }), " of the element and the rules that style it, we'd write ", _jsx(_components.code, {
        children: "Counter"
      }), " like this:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import React from 'react';\nimport styled from 'styled-components';\n\nconst StyledCounter = styled.div`\n  /* ... */\n`;\nconst Paragraph = styled.p`\n  /* ... */\n`;\nconst Button = styled.button`\n  /* ... */\n`;\n\nexport default class Counter extends React.Component {\n  state = { count: 0 };\n\n  increment = () => this.setState({ count: this.state.count + 1 });\n  decrement = () => this.setState({ count: this.state.count - 1 });\n\n  render() {\n    return (\n      <StyledCounter>\n        <Paragraph>{this.state.count}</Paragraph>\n        <Button onClick={this.increment}>+</Button>\n        <Button onClick={this.decrement}>-</Button>\n      </StyledCounter>\n    );\n  }\n}\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Note that we added a \"Styled\" prefix to ", _jsx(_components.code, {
        children: "StyledCounter"
      }), " so that the React component ", _jsx(_components.code, {
        children: "Counter"
      }), " and the Styled Component ", _jsx(_components.code, {
        children: "StyledCounter"
      }), " don't clash names but remain easily identifiable in the React Developer Tools and Web Inspector."]
    }), "\n", _jsx(_components.h3, {
      children: "Define Styled Components outside of the render method"
    }), "\n", _jsx(_components.p, {
      children: "It is important to define your styled components outside of the render method, otherwise it will be recreated on every single render pass. Defining a styled component within the render method will thwart caching and drastically slow down rendering speed, and should be avoided."
    }), "\n", _jsx(_components.p, {
      children: "Write your styled components the recommended way:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "const StyledWrapper = styled.div`\n  /* ... */\n`;\n\nconst Wrapper = ({ message }) => {\n  return <StyledWrapper>{message}</StyledWrapper>;\n};\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "Instead of:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "const Wrapper = ({ message }) => {\n  // WARNING: THIS IS VERY VERY BAD AND SLOW, DO NOT DO THIS!!!\n  const StyledWrapper = styled.div`\n    /* ... */\n  `;\n\n  return <StyledWrapper>{message}</StyledWrapper>;\n};\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Recommended reading"
      }), ": ", _jsx(_components.a, {
        href: "https://twitter.com/talialongname",
        children: "Talia Marcassa"
      }), "\nwrote a great review of real-world usage, featuring lots of solid practical insights\nand comparisons with alternatives, in ", _jsx(_components.a, {
        href: "https://medium.com/building-crowdriff/styled-components-to-use-or-not-to-use-a6bb4a7ffc21",
        children: "Styled Components: To Use or Not to Use?"
      })]
    }), "\n", _jsx(_components.h3, {
      children: "Pseudoelements, pseudoselectors, and nesting"
    }), "\n", _jsxs(_components.p, {
      children: ["The preprocessor we use, ", _jsx(_components.a, {
        href: "https://github.com/thysultan/stylis.js",
        children: "stylis"
      }), ", supports scss-like syntax for automatically nesting styles."]
    }), "\n", _jsx(_components.p, {
      children: "Through this preprocessing, styled-components supports some advanced selector patterns:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "&"
          }), " a single ampersand refers to ", _jsx(_components.strong, {
            children: "all instances"
          }), " of the component; it is used for applying broad overrides:"]
        }), "\n", _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-react",
            children: "const Thing = styled.div.attrs((/* props */) => ({ tabIndex: 0 }))`\n  color: blue;\n\n  &:hover {\n    color: red; // <Thing> when hovered\n  }\n\n  & ~ & {\n    background: tomato; // <Thing> as a sibling of <Thing>, but maybe not directly next to it\n  }\n\n  & + & {\n    background: lime; // <Thing> next to <Thing>\n  }\n\n  &.something {\n    background: orange; // <Thing> tagged with an additional CSS class \".something\"\n  }\n\n  .something-else & {\n    border: 1px solid; // <Thing> inside another element labeled \".something-else\"\n  }\n`\n\nrender(\n  <React.Fragment>\n    <Thing>Hello world!</Thing>\n    <Thing>How ya doing?</Thing>\n    <Thing className=\"something\">The sun is shining...</Thing>\n    <div>Pretty nice day today.</div>\n    <Thing>Don't you think?</Thing>\n    <div className=\"something-else\">\n      <Thing>Splendid.</Thing>\n    </div>\n  </React.Fragment>\n)\n"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "&&"
          }), " a double ampersand refers to ", _jsx(_components.strong, {
            children: "an instance"
          }), " of the component; this is useful if you're doing conditional styling overrides and don't want a style to apply to ", _jsx(_components.em, {
            children: "all instances"
          }), " of a particular component:"]
        }), "\n", _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-react",
            children: "const Input = styled.input.attrs({ type: \"checkbox\" })``;\n\nconst Label = styled.label`\n  align-items: center;\n  display: flex;\n  gap: 8px;\n  margin-bottom: 8px;\n`\n\nconst LabelText = styled.span`\n  ${(props) => {\n    switch (props.$mode) {\n      case \"dark\":\n        return css`\n          background-color: black;\n          color: white;\n          ${Input}:checked + && {\n            color: blue;\n          }\n        `;\n      default:\n        return css`\n          background-color: white;\n          color: black;\n          ${Input}:checked + && {\n            color: red;\n          }\n        `;\n    }\n  }}\n`;\n\nrender(\n  <React.Fragment>\n    <Label>\n      <Input defaultChecked />\n      <LabelText>Foo</LabelText>\n    </Label>\n    <Label>\n      <Input />\n      <LabelText $mode=\"dark\">Foo</LabelText>\n    </Label>\n    <Label>\n      <Input defaultChecked />\n      <LabelText>Foo</LabelText>\n    </Label>\n    <Label>\n      <Input defaultChecked />\n      <LabelText $mode=\"dark\">Foo</LabelText>\n    </Label>\n  </React.Fragment>\n)\n"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "&&"
          }), " a double ampersand alone has a special behavior called a \"precedence boost\"; this can be useful if you are dealing with a mixed styled-components and vanilla CSS environment where there might be conflicting styles:"]
        }), "\n", _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-react",
            children: " const Thing = styled.div`\n   && {\n     color: blue;\n   }\n `\n\n const GlobalStyle = createGlobalStyle`\n   div${Thing} {\n     color: red;\n   }\n `\n\n render(\n   <React.Fragment>\n     <GlobalStyle />\n     <Thing>\n       I'm blue, da ba dee da ba daa\n     </Thing>\n   </React.Fragment>\n )\n"
          })
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "If you put selectors in without the ampersand, they will refer to children of the component."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "const Thing = styled.div`\n  color: blue;\n\n  .something {\n    border: 1px solid; // an element labeled \".something\" inside <Thing>\n    display: block;\n  }\n`\n\nrender(\n  <Thing>\n    <label htmlFor=\"foo-button\" className=\"something\">Mystery button</label>\n    <button id=\"foo-button\">What do I do?</button>\n  </Thing>\n)\n"
      })
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
